import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Package, Filter } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEscapeKey } from '../hooks/useEscapeKey';
import {
    getProductCatalogItems,
    createProductCatalogItem,
    updateProductCatalogItem,
    hardDeleteProductCatalogItem,
    getProductReferences,
    ProductCatalogItem,
    ProductCatalogCreate,
    ProductCatalogUpdate
} from '../utils/productCatalogApi';
import { setInventoryItemStockTracked } from '../utils/inventoryApi';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ProductCatalogItemModal } from '../components/products/ProductCatalogItemModal';

const PRODUCT_TYPE_LABELS: Record<string, string> = {
    'EQUIPMENT': 'Equipment',
    'CONSUMABLE': 'Consumable',
    'FOOD': 'Food',
    'SUPPLEMENT': 'Supplement',
    'ACCESSORY': 'Accessory',
    'OTHER': 'Other'
};

const ProductsCatalogManagement: React.FC = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [items, setItems] = useState<ProductCatalogItem[]>([]);
    const [filteredItems, setFilteredItems] = useState<ProductCatalogItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [selectedItem, setSelectedItem] = useState<ProductCatalogItem | null>(null);
    const [showToggleConfirm, setShowToggleConfirm] = useState(false);
    const [itemToToggle, setItemToToggle] = useState<ProductCatalogItem | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<ProductCatalogItem | null>(null);
    const [deleteReferenceCount, setDeleteReferenceCount] = useState(0);
    const [showInactive, setShowInactive] = useState(() => {
        return localStorage.getItem('products_catalog_showInactive') === 'true';
    });
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const filterMenuRef = useRef<HTMLDivElement>(null);

    const clearModalParams = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('create');
        next.delete('edit');
        setSearchParams(next, { replace: true });
    };

    const closeModal = () => {
        setShowModal(false);
        clearModalParams();
    };
    
    useEscapeKey(() => setShowModal(false), showModal);
    useEscapeKey(() => setShowFilterMenu(false), showFilterMenu);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!showFilterMenu) return;
            const target = event.target as Node;
            if (filterMenuRef.current && !filterMenuRef.current.contains(target)) {
                setShowFilterMenu(false);
            }
        };
        // Use capture so this still runs even if inner elements call stopPropagation()
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [showFilterMenu]);

    const handleShowInactiveChange = (value: boolean) => {
        setShowInactive(value);
        localStorage.setItem('products_catalog_showInactive', String(value));
    };

    useEffect(() => {
        if (user?.practice_id) {
            fetchItems();
        }
    }, [user?.practice_id]);

    // URL-driven behavior:
    // - create=1 opens create modal (kept for now)
    // - edit=<id> redirects to the Stripe-style show page and opens drawer there
    useEffect(() => {
        const create = searchParams.get('create');
        const editId = searchParams.get('edit');
        if (create === '1') {
            handleCreate();
            return;
        }
        if (editId) {
            navigate(`/settings/products-catalog/${editId}?edit=1`, { replace: true });
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, items]);

    useEffect(() => {
        filterItems();
    }, [items, searchTerm, showInactive]);

    const fetchItems = async () => {
        if (!user?.practice_id) return;
        setLoading(true);
        try {
            const data = await getProductCatalogItems(user.practice_id, true);
            setItems(data);
        } catch (err: any) {
            console.error('Failed to fetch catalog:', err);
            alert('Failed to load catalog items: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const filterItems = () => {
        let filtered = items;
        if (!showInactive) {
            filtered = filtered.filter(item => item.is_active);
        }
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.name.toLowerCase().includes(term) ||
                (item.description && item.description.toLowerCase().includes(term)) ||
                (item.sku && item.sku.toLowerCase().includes(term))
            );
        }
        setFilteredItems(filtered);
    };

    const handleCreate = () => {
        setSelectedItem(null);
        setModalMode('create');
        setShowModal(true);
        const next = new URLSearchParams(searchParams);
        next.set('create', '1');
        next.delete('edit');
        setSearchParams(next);
    };

    const handleEdit = (item: ProductCatalogItem) => {
        setSelectedItem(item);
        if (item.is_standard) {
            setModalMode('create');
        } else {
            setModalMode('edit');
        }
        setShowModal(true);
    };

    const handleSave = async (data: ProductCatalogCreate | ProductCatalogUpdate) => {
        if (!user?.practice_id) return;
        try {
            if (modalMode === 'create') {
                // Modal includes based_on_standard_id when customizing a standard item
                await createProductCatalogItem(user.practice_id, data as ProductCatalogCreate);
            } else if (selectedItem) {
                await updateProductCatalogItem(user.practice_id, selectedItem.id, data as ProductCatalogUpdate);
            }
            setShowModal(false);
            await fetchItems();
        } catch (err: any) {
            alert('Failed to save: ' + err.message);
        }
    };

    const handleDeleteClick = async (item: ProductCatalogItem) => {
        if (!user?.practice_id) return;
        try {
            const refs = await getProductReferences(user.practice_id, item.id);
            setDeleteReferenceCount(refs.reference_count);
            setItemToDelete(item);
            setShowDeleteConfirm(true);
        } catch (err) {
            setDeleteReferenceCount(0);
            setItemToDelete(item);
            setShowDeleteConfirm(true);
        }
    };

    const confirmDelete = async () => {
        if (!itemToDelete || !user?.practice_id) return;
        try {
            await hardDeleteProductCatalogItem(user.practice_id, itemToDelete.id);
            await fetchItems();
        } catch (err: any) {
            alert('Failed to delete: ' + err.message);
        } finally {
            setShowDeleteConfirm(false);
            setItemToDelete(null);
        }
    };

    const handleToggleActive = (item: ProductCatalogItem) => {
        setItemToToggle(item);
        setShowToggleConfirm(true);
    };

    const handleToggleConsumable = async (item: ProductCatalogItem, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!user?.practice_id) return;
        
        try {
            const newValue = !item.is_stock_tracked;
            await setInventoryItemStockTracked(user.practice_id, 'product', item.id, newValue);
            setItems(prev => prev.map(i => 
                i.id === item.id ? { ...i, is_stock_tracked: newValue } : i
            ));
        } catch (err: any) {
            alert('Failed to update consumable status: ' + err.message);
        }
    };

    const confirmToggleActive = async () => {
        if (!itemToToggle || !user?.practice_id) return;
        try {
            await updateProductCatalogItem(user.practice_id, itemToToggle.id, {
                is_active: !itemToToggle.is_active
            });
            await fetchItems();
        } catch (err: any) {
            alert('Failed to update item: ' + err.message);
        } finally {
            setShowToggleConfirm(false);
            setItemToToggle(null);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center py-24">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <span className="ml-4 text-lg text-gray-600">Loading catalog...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="bg-teal-100 p-2 rounded-lg">
                            <Package className="w-6 h-6 text-teal-600" />
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900">Products Settings</h1>
                    </div>
                    <p className="text-gray-600 ml-14">
                        Manage products (E-Collars, Supplies, Food). Pricing is managed in the <a href="/settings/catalog" className="text-blue-600 hover:underline">Billing Catalog</a>.
                    </p>
                </div>

                {/* Search and Actions */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1 min-w-64">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <button
                            onClick={handleCreate}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                            <Plus className="w-4 h-4" />
                            Add Product
                        </button>
                    </div>
                </div>

                {/* Filter */}
                <div className="relative mb-4" ref={filterMenuRef}>
                    <button
                        onClick={() => setShowFilterMenu(!showFilterMenu)}
                        className="p-2 rounded-lg border bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 transition-colors"
                    >
                        <Filter className="w-5 h-5" />
                    </button>
                    {showFilterMenu && (
                        <div className="absolute top-full left-0 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 min-w-48">
                            <button
                                onClick={() => { handleShowInactiveChange(!showInactive); setShowFilterMenu(false); }}
                                className="w-full px-4 py-3 text-left text-sm text-gray-200 hover:bg-gray-700 rounded-lg"
                            >
                                {showInactive ? 'Hide Inactive' : 'Show Inactive'}
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Name</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Type</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase">SKU</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase text-center">Consumable</th>
                                    <th className="px-6 py-3 text-xs font-semibold text-gray-600 uppercase text-center">Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredItems.map((item) => (
                                    <tr
                                        key={item.id}
                                        onClick={() => navigate(`/settings/products-catalog/${item.id}`)}
                                        className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${!item.is_active ? 'opacity-50' : ''}`}
                                    >
                                        <td className="px-6 py-4">
                                            <span className="font-medium text-gray-900">{item.name}</span>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {PRODUCT_TYPE_LABELS[item.product_type] || item.product_type}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {item.sku || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={(e) => handleToggleConsumable(item, e)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${item.is_stock_tracked ? 'bg-green-500' : 'bg-gray-300'}`}
                                                title={item.is_stock_tracked ? 'Inventory tracked - click to disable' : 'Not inventory tracked - click to enable'}
                                            >
                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_stock_tracked ? 'translate-x-6' : 'translate-x-1'}`} />
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                onClick={() => handleToggleActive(item)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${item.is_active ? 'bg-green-500' : 'bg-gray-300'}`}
                                            >
                                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active ? 'translate-x-6' : 'translate-x-1'}`} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredItems.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                                            No products found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Edit/Create Modal */}
            <ProductCatalogItemModal
                isOpen={showModal}
                onClose={closeModal}
                onSave={handleSave}
                onRequestDelete={
                    modalMode === 'edit' && selectedItem && !selectedItem.is_standard
                        ? () => { closeModal(); handleDeleteClick(selectedItem); }
                        : undefined
                }
                item={selectedItem}
                mode={modalMode}
            />

            {/* Toggle Confirm */}
            <ConfirmDialog
                isOpen={showToggleConfirm}
                title={itemToToggle?.is_active ? "Deactivate Product" : "Activate Product"}
                message={itemToToggle?.is_active ? `Deactivate "${itemToToggle?.name}"?` : `Activate "${itemToToggle?.name}"?`}
                confirmText={itemToToggle?.is_active ? "Deactivate" : "Activate"}
                onConfirm={confirmToggleActive}
                onClose={() => { setShowToggleConfirm(false); setItemToToggle(null); }}
            />

            {/* Delete Confirm */}
            {deleteReferenceCount === 0 && (
                <ConfirmDialog
                    isOpen={showDeleteConfirm}
                    title="Delete Product"
                    message={`Delete "${itemToDelete?.name}"? This will also remove the linked billing item.`}
                    confirmText="Delete"
                    onConfirm={confirmDelete}
                    onClose={() => { setShowDeleteConfirm(false); setItemToDelete(null); }}
                    isDangerous
                />
            )}
            {deleteReferenceCount > 0 && showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Cannot Delete</h2>
                        <p className="text-gray-600 mb-4">{deleteReferenceCount} orders reference this item. Remove them first.</p>
                        <button onClick={() => { setShowDeleteConfirm(false); setItemToDelete(null); }} className="px-4 py-2 bg-gray-200 rounded-md">OK</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductsCatalogManagement;

